import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../shared/services/auth.service';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Meeting} from '../../../shared/interface/meeting';
import {MeetingService} from '../../../shared/services/meeting.service';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {
  MEETING_TYPE_NEW_AGENT_TRAINING,
  MEETING_TYPE_IDD_TRAINING,
  MEETING_TYPE_MEETING,
  MEETING_TYPE_OTHER,
  MEETING_TYPE_ADA,
  MEETING_TYPE_TRAINING,
  MEETING_TYPE_EXAM,
  MEETING_TYPE_ENTRANCE_EXAM,
} from '../../../shared/services/dict.service';
@Component({
  selector: 'app-all-meetings-list',
  templateUrl: './all-meetings-list.component.html',
  styleUrls: ['./all-meetings-list.component.scss']
})
export class AllMeetingsListComponent implements AfterViewInit, OnDestroy {
  readonly MEETING_TYPES = [MEETING_TYPE_IDD_TRAINING, MEETING_TYPE_NEW_AGENT_TRAINING,
    MEETING_TYPE_ADA, MEETING_TYPE_TRAINING, MEETING_TYPE_MEETING,
    MEETING_TYPE_OTHER, MEETING_TYPE_NEW_AGENT_TRAINING];

  @Input() name?: string;
  @Input() tableFor: string;
  @Input() displayedColumns: string[];
  @Input() typeKey?: string[] = null;
  @Input() insuranceCompany?: number;
  @Input() statusKey?: string;
  @Input() disableFilters = false;
  @Input() disableBreadcrumb = false;
  @Input() excludeColumns: string[] = null;
  @Input() missingExamKeys: string[] = null;
  @Input() dialogView = false;
  @Input() embeddedView = false;
  @Output() selectedMeeting: EventEmitter<Meeting> = new EventEmitter<Meeting>();
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  meetings: Meeting[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;

  readonly MEETING_TYPE_EXAM = MEETING_TYPE_EXAM;
  readonly MEETING_TYPE_ENTRANCE_EXAM = MEETING_TYPE_ENTRANCE_EXAM;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private meetingService: MeetingService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.meetingService.meetingListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Meeting>) => {
          this.meetings = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      in$insuranceCompany: 'int[]',
      gte$startsAt: 'date',
      lte$endsAt: 'date',
      in$status: 'int[]',
      in$examType: 'int[]',
      in$place: 'int[]',
      in$leader: 'int[]',
      address: 'string',
      state: 'int',
      type: 'int',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.tableFor = this.tableFor ?? data.tableFor ?? 'Egzamin/Szkolenie';
        this.displayedColumns = this.displayedColumns ?? data.displayedColumns ?? [];
        this.typeKey = this.typeKey ?? data.typeKey ?? null;
        this.insuranceCompany = this.insuranceCompany ?? data.insuranceCompany ?? null;
        this.statusKey = this.statusKey ?? data.statusKey ?? null;
        this.missingExamKeys = this.missingExamKeys ?? data.missingExamKeys ?? null;
        this.name = this.name ?? data.name ?? 'Egzaminy i szkolenia';

        if (!this.embeddedView) {
          this.store
            .pipe(
              select(getCurrentRouteState),
              delay(0),
              distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
              takeUntil(this.destroyed$),
            )
            .subscribe((state: RouterStateUrl) => {
              this.parseFilters(state.params);
              this.paginator.pageIndex = state.params.page;
              this.paginator.pageSize = state.params.pageSize;
              this.loadListData();
            })
          ;
        } else {
          this.loadListData();
        }
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        if (this.embeddedView) {
          this.loadListData();
        } else {
          this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
            page: event.pageIndex,
            pageSize: event.pageSize,
            ...this.filtersForm.value
          })]);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.meetingService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.typeKey) {
      filters.typeKey = this.typeKey;
    }

    if (null !== this.insuranceCompany) {
      filters.insuranceCompany = this.insuranceCompany;
    }

    if (null !== this.statusKey) {
      filters.statusKey = this.statusKey;
    }

    if (null !== this.missingExamKeys) {
      filters.missingExamKeys = this.missingExamKeys;
    }

    return filters;
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })]);
    }
  }

  downloadReport(): void {
    this.meetingService.downloadReport(this.buildFilters());
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  delete(meeting: Meeting): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć obiekt?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.meetingService.delete(meeting.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }
}
